/**
 * FormController — put it on a <form>. It owns ONE form: JSON submission through `this.http`,
 * dirty tracking, the double-submit guard, error placement and reset. Forms are ALWAYS JSON:
 * a named file input is a configuration error (files go through a separate upload component that keeps
 * the uploaded id in a hidden input).
 *
 * Hooks (implement what you need; `start`/`clear`/`setup` are the BaseController hooks):
 *   start()          load the record, then `this.load(data)` (writes the values and takes the baseline)
 *   save(payload)    return the http result; the default PATCH/POST/PUT-es `config.url` (or the form action)
 *   savedNotice(data) after an ok save, including when newer edits remain dirty
 *   saved(data)      after an ok save only when clean; may clear fields or close the drawer
 *   showErrors(err)  place validation errors; unknown / non-field errors go to the summary
 * API: `dirty` · `commit()` · `reset()` · `setValue(name, value)` · `load(data)`
 * Events: `form:dirty-changed` { dirty } and `form:saved` (fixed `form:` prefix so a drawer or shell can
 * listen without knowing the subclass identifier). Subclasses may emit their own `<identifier>:<event>`.
 *
 *   <form data-controller="lead-form" data-lead-form-url-value="/api/leads/7/" data-lead-form-start-value="connect">
 *     <div data-lead-form-target="summary" role="alert" tabindex="-1" hidden>
 *       <p data-kind="network timeout" hidden>…copy written in the template…</p>
 *     </div>
 *     <input name="email"> <p data-error-for="email" hidden></p>
 *   </form>
 */
import { BaseController } from './base_controller.js'
import { emit } from './events.js'

/** A comparable picture of what the form would submit: hidden inputs included. */
function snapshot(form) {
  return JSON.stringify([...new FormData(form)].map(([name, value]) => [name, String(value)]))
}

export class FormController extends BaseController {
  static targets = ['summary']
  static config = {
    method: { type: 'enum', oneOf: ['POST', 'PUT', 'PATCH'], default: 'POST' },
  }

  #baseline = ''
  /** @type {Map<HTMLInputElement, string>} */
  #hidden = new Map()
  /** @type {(() => void)[]} */
  #marks = []
  #submitting = false
  #resetting = false
  #submitCleanups = new Set()

  /** Fail closed: even with an invalid config the browser must never submit this form natively. */
  safeguard() {
    if (this.element instanceof HTMLFormElement) this.listen(this.element, 'submit', (event) => event.preventDefault())
  }

  prepare() {
    const form = this.element
    if (!(form instanceof HTMLFormElement)) return { kind: 'config', message: '' }
    if (form.querySelector('input[type="file"][name]')) return { kind: 'config', message: '' } // nameless upload inputs never enter FormData
    this.commit()
    const check = () => this.checkDirty()
    this.listen(form, 'input', check)
    this.listen(form, 'change', check)
    // Writing .value of a hidden input changes its value attribute and fires no event; fields can also be
    // added or removed. The observer sees both.
    this.observe(new MutationObserver(check), form, { subtree: true, childList: true, attributes: true, attributeFilter: ['value'] })
    this.listen(form, 'submit', (event) => this.submit(event))
    this.listen(form, 'reset', (event) => {
      if (this.#resetting) return
      event.preventDefault()
      this.reset()
    })
    this.listen(window, 'beforeunload', (event) => {
      if (this.dirty) event.preventDefault()
    })
  }

  /** A dirty (or saving) form keeps its values: a config change waits for save / discard. */
  get restartBlocked() {
    return this.dirty || this.#submitting
  }

  get dirty() {
    return this.element.hasAttribute('data-dirty')
  }

  get submitting() {
    return this.#submitting
  }

  /** Re-compare with the baseline. Call it after changing the form from code (setValue() does). */
  checkDirty() {
    const dirty = snapshot(this.element) !== this.#baseline
    if (dirty === this.dirty) return dirty
    this.element.toggleAttribute('data-dirty', dirty)
    emit(this.element, 'form', 'dirty-changed', { dirty, form: this.element.id || null })
    if (!dirty) this.resumeRestart()
    return dirty
  }

  /** Capture before submission disables controls; subclasses may append their own baseline data. */
  captureState() {
    return {
      snapshot: snapshot(this.element),
      controls: [...this.element.elements].map((control) => ({
        control,
        value: control.value,
        checked: control.checked,
        options: control.options ? [...control.options].map((option) => [option, option.selected]) : null,
      })),
    }
  }

  /** Commit a captured state, or the current form when loading/defaulting. Keep live edits intact. */
  commit(state = this.captureState()) {
    this.#hidden.clear()
    const live = this.captureState()
    for (const { control, value, checked, options } of state.controls) {
      if (control.type === 'hidden') this.#hidden.set(control, value)
      else if (control.type === 'checkbox' || control.type === 'radio') control.defaultChecked = checked
      else if (options) for (const [option, selected] of options) option.defaultSelected = selected
      else if ('defaultValue' in control) control.defaultValue = value
    }
    // Changing defaults can change pristine native controls. Restore their current values without events.
    for (const { control, value, checked, options } of live.controls) {
      if (control.type === 'checkbox' || control.type === 'radio') control.checked = checked
      else if (options) for (const [option, selected] of options) option.selected = selected
      else if (control.type !== 'hidden' && 'defaultValue' in control) control.value = value
    }
    this.#baseline = state.snapshot
    this.checkDirty()
  }

  /** A control-lock wrapper registers its release here so commit sees the ordinary enabled form. */
  onSubmitSettled(cleanup) {
    this.#submitCleanups.add(cleanup)
    return () => this.#submitCleanups.delete(cleanup)
  }

  #releaseSubmitControls() {
    for (const cleanup of this.#submitCleanups) cleanup()
    this.#submitCleanups.clear()
  }

  /** Back to the last committed state: fields, hidden inputs and errors. Widgets re-read on form:synced after restoration. */
  reset() {
    this.#resetting = true
    try {
      this.element.reset()
    } finally {
      this.#resetting = false
    }
    for (const [input, value] of this.#hidden) if (input.isConnected) input.value = value
    this.clearErrors()
    this.checkDirty()
    emit(this.element, 'form', 'synced', {}, { bubbles: false })
  }

  /**
   * Set one field from code (a widget, `load()`): text/hidden/select by value, a checkbox or radio by
   * checked state (boolean, or the value it carries), a group by an array of values.
   * @param {string} name
   * @param {unknown} value
   */
  setValue(name, value) {
    const found = this.element.elements.namedItem(name)
    const controls = found && !('tagName' in found) ? [...found] : found ? [found] : [] // a RadioNodeList has no tagName
    for (const control of controls) {
      if (control.type === 'checkbox' || control.type === 'radio') {
        control.checked = Array.isArray(value) ? value.map(String).includes(control.value) : typeof value === 'boolean' ? value : String(value ?? '') === control.value
      } else if (control.options && control.multiple) {
        const wanted = (Array.isArray(value) ? value : [value]).map(String)
        for (const option of control.options) option.selected = wanted.includes(option.value)
      } else {
        control.value = value === null || value === undefined ? '' : String(value)
      }
    }
    this.checkDirty()
  }

  /**
   * Write a record into the form, then take it as the baseline (the form is clean afterwards).
   * Only keys that match a field name are used.
   * @param {Record<string, unknown>} data
   */
  load(data) {
    for (const name of Object.keys(data ?? {})) {
      if (Object.hasOwn(data, name)) this.setValue(name, data[name])
    }
    this.commit()
    emit(this.element, 'form', 'synced', {}, { bubbles: false })
  }

  /** What to send: the form as JSON ({name: value | [values]}). Override freely; never FormData. */
  payload() {
    const data = new FormData(this.element)
    const json = Object.create(null)
    for (const name of new Set(data.keys())) {
      const values = data.getAll(name)
      json[name] = values.length > 1 ? values : values[0]
    }
    return json
  }

  /**
   * Hook: send the payload. Called with the double-submit guard already held. Returns an http result.
   * @param {Record<string, unknown>} payload
   */
  save(payload) {
    const url = this.config.url || this.element.getAttribute('action') || ''
    return this.http.request(this.config.method, url, { body: payload })
  }

  /** Hook: announce success even when later edits remain unsaved. @param {unknown} _data */
  savedNotice(_data) {}

  /** Hook: the server accepted the form and there are no later edits to lose. @param {unknown} _data */
  saved(_data) {}

  /** @param {Event} [event] */
  async submit(event) {
    event?.preventDefault()
    if (this.#submitting || !this.lifetime) return
    this.#submitting = true
    const buttons = [...this.element.elements].filter((control) => control.type === 'submit' && !control.disabled)
    this.clearErrors()
    try {
      const sent = this.captureState()
      const payload = this.payload()
      // Submit buttons are disabled for the request; wrappers may lock the rest afterwards.
      for (const button of buttons) button.disabled = true
      const result = await this.save(payload)
      this.#releaseSubmitControls()
      if (!result || result.stale) return
      if (result.ok) {
        this.commit(sent)
        this.savedNotice(result.data)
        emit(this.element, 'form', 'saved', { form: this.element.id || null }) // before the hook: saved() may close the drawer this form lives in
        if (!this.dirty) this.saved(result.data)
      } else {
        this.showErrors(result.error)
      }
    } finally {
      this.#releaseSubmitControls()
      this.#submitting = false
      for (const button of buttons) button.disabled = false
      this.resumeRestart()
    }
  }

  /**
   * Field errors go next to their fields ([data-error-for="<name>"]); everything that has no place goes
   * to the summary (target `summary`), whose `[data-kind]` children carry the per-kind copy. Focus moves
   * to the first invalid field. `error` is an ApiError or `{ kind, message }`.
   * @param {{ kind?: string, message?: string, fieldErrors?: Record<string, string[]> }} error
   */
  showErrors(error) {
    this.clearErrors()
    const slots = new Map([...this.element.querySelectorAll('[data-error-for]')].map((slot) => [slot.getAttribute('data-error-for'), slot]))
    const unplaced = error.message ? [error.message] : []
    let first = null
    for (const [field, messages] of Object.entries(error.fieldErrors ?? {})) {
      const found = this.element.elements.namedItem(field)
      const control = found instanceof Element ? found : (found?.[0] ?? null)
      const slot = slots.get(field)
      if (control) {
        control.setAttribute('aria-invalid', 'true')
        this.#marks.push(() => control.removeAttribute('aria-invalid'))
        first ??= control
      }
      if (slot) {
        slot.textContent = messages.join(' ')
        slot.hidden = false
        this.#marks.push(() => {
          slot.textContent = ''
          slot.hidden = true
        })
      } else {
        unplaced.push(...messages.map((message) => `${field}: ${message}`))
      }
    }
    if (this.hasSummaryTarget) {
      const summary = this.summaryTarget
      const generated = unplaced.map((message) => {
        const line = document.createElement('p')
        line.textContent = message
        return line
      })
      summary.append(...generated)
      for (const part of summary.querySelectorAll('[data-kind]')) {
        const kinds = part.getAttribute('data-kind').split(/\s+/)
        part.hidden = !(kinds.includes('*') || kinds.includes(error.kind))
      }
      summary.hidden = false
      this.#marks.push(() => {
        generated.forEach((line) => line.remove())
        summary.hidden = true
      })
      first ??= summary
    }
    first?.focus()
  }

  clearErrors() {
    for (const undo of this.#marks.splice(0)) undo()
  }

  /** Default: forget errors and unsaved input (a form may hold PII). Override and call super to add more. */
  clear() {
    this.clearErrors()
    this.reset()
  }
}
